import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { getActiveUsers } from "../api/actions";
import {
  EVERYONE_CAN,
  PAGES,
  ROLES,
  STALE_DAYS,
  can,
  canOpen,
  flag,
  fullName,
  hasRole,
  hasRoleRecord,
  isServiceAccount,
  isStale,
  roleName,
} from "../accessRoles";
import PersonDrawer from "./PersonDrawer";
import InvitePersonDialog from "./InvitePersonDialog";

const Dot = ({ state, title }) => (
  <Tooltip title={title}>
    <Box
      component="span"
      aria-label={title}
      sx={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        verticalAlign: "middle",
        bgcolor: state === "on" ? "success.main" : state === "included" ? "success.light" : "transparent",
        opacity: state === "included" ? 0.45 : 1,
        border: state === "off" ? "1.5px solid" : "none",
        borderColor: "divider",
      }}
    />
  </Tooltip>
);

const Tag = ({ label, color = "default" }) => (
  <Chip label={label} size="small" color={color} variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
);

const lastSignIn = (person) => (person.lastLogin ? moment(person.lastLogin).format("MMM D, YYYY") : "Never");

function PeopleAccess(props) {
  const me = props.user.details;
  const users = props.setting.activeUsers.items || [];
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState("active");
  const [filter, setFilter] = useState(null); // a role key, "stale" or "norecord"
  const [query, setQuery] = useState("");
  const [editingUuid, setEditingUuid] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [whoPage, setWhoPage] = useState(0);

  const load = () => {
    setLoading(true);
    Promise.resolve(props.getActiveUsers(false)).finally(() => setLoading(false));
  };

  // Always refresh: complaints and work orders load this list too, but only once.
  useEffect(load, []);

  const people = useMemo(() => {
    const list = users.map((u) => ({
      ...u,
      name: fullName(u),
      active: flag(u.isActive),
      service: isServiceAccount(u),
      stale: isStale(u),
      // Super admins open everything, so a missing role record doesn't matter for them.
      noRecord: !hasRoleRecord(u) && !flag(u.isSuperUser),
    }));
    const counts = {};
    list.filter((p) => p.active && !p.service).forEach((p) => { const k = p.name.toLowerCase(); counts[k] = (counts[k] || 0) + 1; });
    list.forEach((p) => { p.duplicate = p.active && !p.service && counts[p.name.toLowerCase()] > 1; });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const inShow = (p) => show === "all" || (show === "active" ? p.active : !p.active);
  const matches = (p) => {
    if (filter === "stale" && !p.stale) return false;
    if (filter === "norecord" && !p.noRecord) return false;
    if (filter && filter !== "stale" && filter !== "norecord" && !hasRole(p, filter)) return false;
    const q = query.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || String(p.email || "").toLowerCase().includes(q);
  };
  const visible = people.filter((p) => inShow(p) && matches(p));
  const staff = visible.filter((p) => !p.service);
  const services = visible.filter((p) => p.service);
  const countBase = people.filter((p) => inShow(p) && !p.service);

  const staleCount = people.filter((p) => p.stale).length;
  const noRecordCount = people.filter((p) => p.active && p.noRecord && !p.service).length;
  const duplicateNames = [...new Set(people.filter((p) => p.duplicate).map((p) => p.name))];
  const editing = people.find((p) => p.uuid === editingUuid) || null;

  const showStale = () => { setShow("active"); setFilter("stale"); };
  const filterLabel = filter === "stale" ? `no sign-in for over a year` : filter === "norecord" ? "no roles set" : filter ? roleName(filter) : null;

  const renderRow = (p) => (
    <TableRow
      key={p.uuid}
      hover
      tabIndex={0}
      onClick={() => setEditingUuid(p.uuid)}
      onKeyDown={(e) => { if (e.key === "Enter") setEditingUuid(p.uuid); }}
      sx={{ cursor: "pointer", "& td": { color: p.service ? "text.secondary" : undefined } }}
    >
      <TableCell sx={{ minWidth: 220 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.name}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">{p.email}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {!p.active && <Tag label="Inactive" />}
          {p.stale && <Tag label={p.lastLogin ? `No sign-in for ${Math.floor(moment().diff(moment(p.lastLogin), "days") / 365)}+ yr` : "Never signed in"} color="warning" />}
          {p.noRecord && !p.service && <Tag label="No roles set" color="error" />}
          {p.duplicate && <Tag label="Two accounts" color="warning" />}
          {p.service && <Tag label="Service account" />}
        </Box>
      </TableCell>
      {ROLES.map((role) => {
        let state = "off";
        let title = `No ${role.name}`;
        if (role.key === "isSuperUser") {
          if (flag(p.isSuperUser)) { state = "on"; title = "Super admin"; }
        } else if (flag(p.isSuperUser)) {
          state = "included"; title = "Included in Super admin";
        } else if (flag(p[role.key])) {
          state = "on"; title = role.name;
        }
        return (
          <TableCell key={role.key} align="center">
            {p.noRecord && role.key !== "isSuperUser"
              ? <Typography variant="caption" color="text.secondary" title="No roles set">–</Typography>
              : <Dot state={state} title={title} />}
          </TableCell>
        );
      })}
      <TableCell sx={{ whiteSpace: "nowrap", color: p.stale ? "warning.dark" : undefined, fontWeight: p.stale ? 500 : undefined }}>
        {lastSignIn(p)}
      </TableCell>
    </TableRow>
  );

  const who = PAGES[whoPage];
  const whoList = people.filter((p) => p.active && !p.service && canOpen(p, who));

  if (loading && people.length === 0) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={420} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {(staleCount > 0 || noRecordCount > 0 || duplicateNames.length > 0) && (
        <Box sx={{ display: "grid", gap: 1 }}>
          {staleCount > 0 && (
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={showStale} sx={{ textTransform: "none" }}>Review them</Button>}>
              <b>{staleCount} {staleCount === 1 ? "person" : "people"}</b> can still sign in but haven't in over a year.
            </Alert>
          )}
          {noRecordCount > 0 && (
            <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => { setShow("active"); setFilter("norecord"); }} sx={{ textTransform: "none" }}>Show them</Button>}>
              <b>{noRecordCount}</b> active {noRecordCount === 1 ? "person has" : "people have"} no roles set. Open someone to give them roles.
            </Alert>
          )}
          {duplicateNames.length > 0 && (
            <Alert severity="warning">
              {duplicateNames.join(", ")} {duplicateNames.length === 1 ? "has" : "have"} more than one active account.
            </Alert>
          )}
        </Box>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5, px: 2, py: 1.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={show}
            onChange={(e, v) => { if (v) { setShow(v); if (filter === "stale") setFilter(null); } }}
            aria-label="Show"
          >
            <ToggleButton value="active" sx={{ textTransform: "none", px: 1.5 }}>Active</ToggleButton>
            <ToggleButton value="disabled" sx={{ textTransform: "none", px: 1.5 }}>Inactive</ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>All</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <TextField size="small" placeholder="Find a person" value={query} onChange={(e) => setQuery(e.target.value)} inputProps={{ "aria-label": "Find a person" }} sx={{ width: 200 }} />
            {can(me, "inviteStaff") && (
              <Button variant="contained" color="secondary" size="small" onClick={() => setInviting(true)} sx={{ textTransform: "none" }}>Invite person</Button>
            )}
          </Box>
        </Box>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                {ROLES.map((role) => {
                  const pressed = filter === role.key;
                  return (
                    <TableCell key={role.key} align="center" sx={{ width: 96, p: 0.5 }}>
                      <ButtonBase
                        onClick={() => setFilter(pressed ? null : role.key)}
                        aria-pressed={pressed}
                        title={pressed ? "Show everyone" : `Show only ${role.name}`}
                        sx={{
                          display: "grid",
                          justifyItems: "center",
                          width: "100%",
                          px: 0.5,
                          py: 0.75,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          bgcolor: pressed ? "action.selected" : "transparent",
                          boxShadow: pressed ? "inset 0 -2px 0 #d67104" : "none",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {role.name}
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {countBase.filter((p) => hasRole(p, role.key)).length}
                        </Typography>
                      </ButtonBase>
                    </TableCell>
                  );
                })}
                <TableCell>Last sign-in</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map(renderRow)}
              {services.length > 0 && (
                <TableRow>
                  <TableCell colSpan={ROLES.length + 2} sx={{ bgcolor: "action.hover", py: 0.75 }}>
                    <Typography variant="caption" sx={{ letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}>
                      Service accounts: used by the nightly batch and system jobs
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {services.map(renderRow)}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={ROLES.length + 2}>
                    <Typography variant="body2" color="text.secondary">Nobody matches.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {staff.length} {show === "active" ? "active" : show === "disabled" ? "inactive" : ""} {staff.length === 1 ? "person" : "people"}
            {services.length > 0 && ` and ${services.length} service ${services.length === 1 ? "account" : "accounts"}`}
            {filterLabel && ` · ${filterLabel}`}. Faint dots are included in Super admin. Click a column to filter, a person to edit.
          </Typography>
          {filter && <Button size="small" onClick={() => setFilter(null)} sx={{ textTransform: "none" }}>Clear filter</Button>}
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", lg: "minmax(0,1.3fr) minmax(0,1fr)" }, gap: 2, alignItems: "start" }}>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>What each role opens</Typography>
            <Typography variant="caption" color="text.secondary">
              Roles hide pages and buttons in Ubil+ (the API doesn't check them yet). A change takes effect the next time the person signs in.
            </Typography>
          </Box>
          {[...ROLES, { key: "everyone", name: "Everyone", opens: EVERYONE_CAN }].map((role) => (
            <Box key={role.key} sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "120px minmax(0,1fr)" }, gap: 0.5, px: 2.5, py: 1.25, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{role.name}</Typography>
              <Typography variant="body2" color="text.secondary">{role.opens}</Typography>
            </Box>
          ))}
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, display: "grid", gap: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Who can open…</Typography>
          <TextField select size="small" value={whoPage} onChange={(e) => setWhoPage(Number(e.target.value))} inputProps={{ "aria-label": "Page or action" }}>
            {PAGES.map((page, i) => <MenuItem key={page.label} value={i}>{page.label}</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            {!who.capability
              ? `No role needed: everyone who can sign in (${whoList.length}).`
              : `Needs ${who.roles.length ? `${who.roles.map(roleName).join(" or ")} (or Super admin)` : "Super admin"}. ${whoList.length} active ${whoList.length === 1 ? "person" : "people"}. Dashed: no sign-in for over ${STALE_DAYS / 365} year.`}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {whoList.map((p) => (
              <Chip
                key={p.uuid}
                label={p.name}
                size="small"
                variant="outlined"
                onClick={() => setEditingUuid(p.uuid)}
                sx={p.stale ? { borderStyle: "dashed", color: "warning.dark" } : undefined}
              />
            ))}
          </Box>
        </Paper>
      </Box>

      <PersonDrawer person={editing} me={me} onClose={() => setEditingUuid(null)} />
      <InvitePersonDialog open={inviting} onClose={() => setInviting(false)} />
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    setting: state.setting,
    user: state.user,
  };
}

export default connect(mapStateToProps, { getActiveUsers })(PeopleAccess);
